import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useCurrentPageType } from "shared/hooks";
import { PageType } from "shared/lib/page-type";

import { AgendaContent } from "scmp-app/components/plus/home/agenda-block/content";
import { useNewsAgenda } from "scmp-app/components/plus/news-agenda/hooks";
import { PlusWidgetTheme } from "scmp-app/components/plus/plus-widget";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { pathname as plusAgendaPathname } from "scmp-app/pages/plus/agenda";
import type { itemAgendaItemTopic$key } from "scmp-app/queries/__generated__/itemAgendaItemTopic.graphql";

import { Container, StyledBaseLink } from "./styles";
type Props = {
  className?: string;
  gaEventType?: "discovery_module" | "plus_home";
  isFirstAgenda?: boolean;
  isFirstKeyEventAgenda?: boolean;
  needToShowUpdatedLabel?: boolean;
  plusWidgetTheme: PlusWidgetTheme;
  reference: itemAgendaItemTopic$key;
  showAdditionalContent?: boolean;
};

export const AgendaItem: FunctionComponent<Props> = ({
  className,
  gaEventType = "plus_home",
  isFirstKeyEventAgenda,
  needToShowUpdatedLabel,
  plusWidgetTheme = PlusWidgetTheme.DARK,
  reference,
  showAdditionalContent = true,
}) => {
  const data = useFragment(
    graphql`
      fragment itemAgendaItemTopic on Topic {
        urlAlias
        newsAgenda {
          startDate
        }
        entityId
        name
        ...contentAgendaContentTopic
        ...hooksNewsAgendaTopic
      }
    `,
    reference,
  );

  const { date, hasDetailsView: clickable } = useNewsAgenda(data);
  const currentPageType = useCurrentPageType();

  const handleGA4Tracking = () => {
    if (gaEventType === "discovery_module") {
      const pageType = currentPageType === PageType.Homepage ? "homepage" : "section";
      sendGA4Tracking({
        action: "click",
        category: "recirculation",
        customized_parameters: {
          block_section: "new_agenda",
          destination_url: data?.urlAlias ?? "",
          page_type: pageType,
          topic_id: data?.entityId ?? "",
          widget_name: "content_discovery",
          widget_version: "subscriber",
        },
        subcategory: "widget",
      });
    } else {
      sendGA4Tracking({
        action: "click",
        category: "menu",
        customized_parameters: {
          agenda_event: data.name,
          agenda_event_date: date ?? "",
          page_type: "homepage",
          topic_id: data.entityId,
        },
        subcategory: "agenda",
      });
    }
  };

  return (
    <Container>
      {clickable ? (
        <StyledBaseLink
          as={data.urlAlias}
          onClick={handleGA4Tracking}
          pathname={plusAgendaPathname}
          query={{
            entityId: data.entityId,
            startDate: data.newsAgenda?.startDate?.toFixed(0) ?? undefined,
          }}
        >
          <AgendaContent
            className={className}
            isFirstKeyEventAgenda={isFirstKeyEventAgenda}
            needToShowUpdatedLabel={needToShowUpdatedLabel}
            plusWidgetTheme={plusWidgetTheme}
            reference={data}
            showAdditionalContent={showAdditionalContent}
          />
        </StyledBaseLink>
      ) : (
        <AgendaContent
          className={className}
          isFirstKeyEventAgenda={isFirstKeyEventAgenda}
          needToShowUpdatedLabel={needToShowUpdatedLabel}
          plusWidgetTheme={plusWidgetTheme}
          reference={data}
          showAdditionalContent={showAdditionalContent}
        />
      )}
    </Container>
  );
};

AgendaItem.displayName = "AgendaItem";
